// eslint-disable-next-line no-restricted-imports
import type { Channels, PaletteColor } from '@mui/material/styles/createPalette'
import type { ColorSystemOptions } from '@mui/material/styles'
import { themeConfig } from '../theme-config'
import { ThemeColorScheme } from '../types'
import { varAlpha, createPaletteChannel } from 'minimal-shared/utils'

// --------------------------------------------------------------------------------------------- //

/**
 * TypeScript (type definition and extension)
 * @to {@link file://./../extend-theme-types.d.ts}
 */

// Keys for the palette colors
export type PaletteColorKey = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error'

// Palette color without additional channels
export type PaletteColorNoChannels = Omit<PaletteColor, 'lighterChannel' | 'darkerChannel'>

// Palette color with additional channels
export type PaletteColorWithChannels = PaletteColor & Channels

// Extended common colors
export type CommonColorsExtend = {
  whiteChannel: string
  blackChannel: string
}

// Extended text colors
export type TypeTextExtend = {
  disabledChannel: string
}

// Extended background colors
export type TypeBackgroundExtend = {
  neutral: string
  neutralChannel: string
}

// Extended palette colors
export type PaletteColorExtend = {
  lighter?: string
  darker?: string
  lighterChannel?: string
  darkerChannel?: string
}

// Extended grey channels
export type GreyExtend = {
  '50Channel': string
  '100Channel': string
  '200Channel': string
  '300Channel': string
  '400Channel': string
  '500Channel': string
  '600Channel': string
  '700Channel': string
  '800Channel': string
  '900Channel': string
}

// --------------------------------------------------------------------------------------------- //

// Primary color
export const primary = createPaletteChannel(themeConfig.palette.primary)

// Secondary color
export const secondary = createPaletteChannel(themeConfig.palette.secondary)

// Info color
export const info = createPaletteChannel(themeConfig.palette.info)

// Success color
export const success = createPaletteChannel(themeConfig.palette.success)

// Warning color
export const warning = createPaletteChannel(themeConfig.palette.warning)

// Error color
export const error = createPaletteChannel(themeConfig.palette.error)

// Common color
export const common = createPaletteChannel(themeConfig.palette.common)

// Grey color
export const grey = createPaletteChannel(themeConfig.palette.grey)

// Text color
export const text = {
  light: createPaletteChannel({ primary: grey[800], secondary: grey[600], disabled: grey[500] }),
  dark: createPaletteChannel({ primary: '#FFFFFF', secondary: grey[500], disabled: grey[600] })
}

// Background color
export const background = {
  light: createPaletteChannel({ paper: '#FFFFFF', default: '#FFFFFF', neutral: grey[200] }),
  // dark: createPaletteChannel({ paper: grey[800], default: grey[900], neutral: '#28323D' })
  dark: createPaletteChannel({ paper: '#16191c', default: '#101214', neutral: '#1c2024' })
}

// Base action color
export const baseAction = {
  hover: varAlpha(grey['500Channel'], 0.08),
  selected: varAlpha(grey['500Channel'], 0.16),
  focus: varAlpha(grey['500Channel'], 0.24),
  disabled: varAlpha(grey['500Channel'], 0.8),
  disabledBackground: varAlpha(grey['500Channel'], 0.24),
  hoverOpacity: 0.08,
  disabledOpacity: 0.48
}

// Action color
export const action = {
  light: { ...baseAction, active: grey[600] },
  dark: { ...baseAction, active: grey[500] }
}
// --------------------------------------------------------------------------------------------- //

/*
 * Custom Palette Colors
 */
export const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4']
}

export const ORDER_SIDE_COLORS = {
  BUY: success.dark,
  SELL: error.dark
}
export const ORDER_TYPE_COLORS = {
  LIMIT: warning.dark,
  MARKET: info.dark
}

export const ORDER_STATUS_COLORS = {
  OPEN: info.darker,
  CLOSED: success.dark,
  CANCELED: grey['600']
}

export const ORDER_STATUS_TEXT_COLOR = {
  OPEN: info.main,
  CLOSED: success.main,
  CANCELED: warning.main
}

export const CUSTOM_PALETTE_COLORS = {
  chart: CHART_COLORS,
  orderSide: ORDER_SIDE_COLORS,
  orderType: ORDER_TYPE_COLORS,
  orderStatus: ORDER_STATUS_COLORS,
  orderStatusText: ORDER_STATUS_TEXT_COLOR
}

// --------------------------------------------------------------------------------------------- //

// Base palette
export const basePalette = {
  primary,
  secondary,
  info,
  success,
  warning,
  error,
  common,
  grey,
  divider: varAlpha(grey['500Channel'], 0.2)
}

export const palette: Record<ThemeColorScheme, ColorSystemOptions['palette']> = {
  light: {
    ...basePalette,
    ...CUSTOM_PALETTE_COLORS,
    text: text.light,
    background: background.light,
    action: action.light
  },
  dark: {
    ...basePalette,
    ...CUSTOM_PALETTE_COLORS,
    text: text.dark,
    background: background.dark,
    action: action.dark
  }
}
